import { cn } from "@aurem/ui";
import { isInRect } from "@aurem/utils";
import type { FC, JSX, MouseEvent, PropsWithChildren, RefObject } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

type SetContentFn = (content: JSX.Element) => void;

export type BottomSheetProps = {
  dialogRef: RefObject<HTMLDialogElement | null>;
  setContentRef: RefObject<SetContentFn | null>;
  close: () => void;
};

export const BottomSheet: FC<BottomSheetProps> = ({
  dialogRef,
  setContentRef,
  close,
}) => {
  const [content, setContent] = useState<JSX.Element>();

  useEffect(() => {
    setContentRef.current = setContent;
  }, [setContentRef, setContent]);

  function handleClick(e: MouseEvent) {
    const rect = dialogRef.current?.getBoundingClientRect();
    if (!rect || isInRect(rect, e.clientX, e.clientY)) return;
    close();
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={handleClick}
      className={cn(
        "z-50",
        // Animations
        "transition-all-dialog backdrop:transition-all-dialog",
        "translate-y-full starting:open:translate-y-full open:translate-y-0",
        "open:duration-500 duration-300 ease-in-out",
        "backdrop:opacity-0 backdrop:bg-black starting:backdrop:open:opacity-0 backdrop:open:opacity-80",
        // General styles
        "max-w-full w-screen p-0 m-0 box-border bg-background-100 mt-auto rounded-ss-2xl rounded-se-2xl pt-6 pb-10 outline-none",
        // TODO(Jakub): add desktop styles
      )}
    >
      {content}
    </dialog>
  );
};

type BottomSheetMethods = {
  open: (content: JSX.Element) => void;
  close: () => void;
  setContent: (content: JSX.Element) => void;
};

const BottomSheetCtx = createContext<BottomSheetMethods | null>(null);

export function useBottomSheet(): BottomSheetMethods {
  const ctx = useContext(BottomSheetCtx);
  if (ctx == null) {
    throw new Error(
      "useBottomSheet must be called from withing a BottomSheetProvider",
    );
  }
  return ctx;
}

export const BottomSheetProvider: FC<PropsWithChildren> = ({ children }) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const setContentRef = useRef<SetContentFn | null>(null);

  function setContent(content: JSX.Element) {
    setContentRef.current?.(content);
  }

  function open(content: JSX.Element) {
    setContent(content);
    dialogRef.current?.showModal();
  }

  function close() {
    dialogRef.current?.close();
  }

  return (
    <BottomSheetCtx.Provider value={{ open, close, setContent }}>
      <BottomSheet
        dialogRef={dialogRef}
        setContentRef={setContentRef}
        close={close}
      />
      {children}
    </BottomSheetCtx.Provider>
  );
};
